import React, { useState, useMemo, useEffect, FormEvent } from "react";
import { CardElement, CardElementProps } from "@stripe/react-stripe-js";
import { Theme } from "@material-ui/core";
import Grid from "@material-ui/core/Grid";
import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import Spinner from "../../../shared/components/Spinner";
import { useTheme } from "@material-ui/styles";
import { useStripeComponents } from "../../contexts/StripeProvider";
import {
  confirmCardPayment,
  StripePaymentResult,
} from "../hooks/useStripePayment";
import { makeRootStyles } from "../../theme/styles";
import { config } from "../../config";
import { StripeCardElementChangeEvent } from "@stripe/stripe-js";

const useStyles = makeRootStyles(
  (theme: Theme) => ({
    stripeElement: {
      padding: "10px 20px",
      backgroundColor: "white",
      transition: "box-shadow 150ms ease",
      marginBottom: theme.spacing(2),
      width: "100%",
      border: "1px solid #e8e8fb",
      pointerEvents: (props: { disabled: boolean }) =>
        props.disabled ? "none" : "auto",
    },
    form: {
      width: "100%",
    },
    submitContainer: {
      marginTop: theme.spacing(4),
      width: "100%",
      borderRadius: 4,
      border: "1px solid #e8e8fb",
      padding: theme.spacing(4),
    },
    buttonContainer: {
      width: "100%",
    },
    submitButton: {
      marginBottom: theme.spacing(2),
      width: "100%",
    },
    errorText: {
      color: theme.palette.error.main,
    },
  }),
  { name: "N0StripeCheckoutForm" },
);

export interface StripeProps {
  secret: string;
  onSuccess?: (result: StripePaymentResult) => void;
  hideSubmit?: boolean;
  submitLoading: boolean;
  backendPaymentFailureError?: string;
}

const Stripe: React.FC<StripeProps> = ({
  secret,
  onSuccess,
  hideSubmit = false,
  submitLoading,
  backendPaymentFailureError = "",
}) => {
  const theme: Theme = useTheme();
  const { stripe, elements } = useStripeComponents();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>(backendPaymentFailureError);
  const classes = useStyles();

  const cardElementOptions: CardElementProps["options"] = useMemo(
    () => ({
      style: {
        base: {
          fontFamily: theme.typography.body1.fontFamily,
          fontSize: `${theme.typography.body1.fontSize}px`,
          color: theme.palette.text.primary,
          fontSmoothing: "antialiased",
          "::placeholder": {
            color: theme.palette.text.hint,
          },
        },
        invalid: {
          color: "#fa755a",
          iconColor: "#fa755a",
        },
      },
    }),
    [theme],
  );

  useEffect(() => {
    setError(backendPaymentFailureError);
  }, [backendPaymentFailureError]);

  const handleChange = (event: StripeCardElementChangeEvent) => {
    setError(event.error?.message ?? "");
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) return;

    setLoading(true);
    setError("");

    confirmCardPayment({ stripe, elements, secret })
      .then((result) => {
        setLoading(false);
        onSuccess?.(result);
      })
      .catch((error: Error) => {
        setLoading(false);
        setError(error.message);
      });
  };

  return (
    <form onSubmit={handleSubmit} className={classes.form}>
      <Grid
        className={classes.submitContainer}
        container
        direction="column"
        justifyContent="center"
        alignItems="center">
        <CardElement
          className={classes.stripeElement}
          options={cardElementOptions}
          onChange={handleChange}
        />

        {!hideSubmit && (
          <Grid xs={12} sm={6} md={4} item className={classes.buttonContainer}>
            <Button
              className={classes.submitButton}
              color="primary"
              disabled={submitLoading || loading}
              size="large"
              variant="contained"
              type="submit"
              fullWidth>
              <strong>{config.translations.return_review_submit_return}</strong>
            </Button>
          </Grid>
        )}
        {!hideSubmit && (submitLoading || loading) && (
          <Grid xs={12} item>
            <Spinner />
          </Grid>
        )}
        {error && (
          <Grid xs={12} item>
            <Typography
              align="center"
              variant="body1"
              className={classes.errorText}>
              {error}
            </Typography>
          </Grid>
        )}
      </Grid>
    </form>
  );
};

export default Stripe;
